import { type MouseEvent } from 'react';

import {
  CONTENT_TYPES,
  type ContentListItemType,
  type ItemTypeMap,
} from '@hivebrite/design-system/ContentList';

import { makeConfigUrls as makeForumUrls } from 'hb-react/frontoffice/apps/Forum/utils/configUrls';
import { getEventDedicatedPath } from 'hb-react/shared/apps/Events/utils/getEventDedicatedUrl';
import getDateStr from 'hb-react/shared/components/EventDate/utils/getDateStr';
import dayjs from 'hb-react/shared/utils/dayjs/dayjs';
import {
  ENTITY_TYPES,
  LEGACY_ENTITY_TYPES,
} from 'hb-react/shared/utils/entitiesContentData/constants';
import {
  type EntityItem,
  type ForumDiscussion,
  type AdminEvent,
  type News,
  type UserEvent,
} from 'hb-react/shared/utils/entitiesContentData/types';
import { type TimeZoneConfiguration } from 'hb-react/types/timeZoneConfiguration';

interface TransformerOptions {
  displayFields?: {
    [K in CONTENT_TYPES]?: ItemTypeMap[K]['displayFields'];
  };
  flags?: Record<string, boolean>;
  isPublicOffice?: boolean;
  isWhiteLabelGroup?: boolean;
  timeZoneConfiguration?: TimeZoneConfiguration;
  withLink?: boolean;
}

// Helper to transform common event data
const transformCommonEventData = (
  item: AdminEvent | UserEvent,
  options: TransformerOptions,
) => {
  const {
    displayFields,
    isPublicOffice,
    isWhiteLabelGroup,
    timeZoneConfiguration = {},
    withLink,
  } = options;
  const { currentUserTimezone, eventDatesInUserTimezone } =
    timeZoneConfiguration;
  const timezone = eventDatesInUserTimezone
    ? currentUserTimezone
    : item.timezone;

  const date = getDateStr({
    startDate: item.startDate,
    endDate: undefined,
    timezone,
  });

  return {
    contentType: CONTENT_TYPES.EVENT,
    date,
    displayFields: displayFields?.[CONTENT_TYPES.EVENT],
    id: item.id,
    location: item?.location?.location,
    formattedDate: {
      day: dayjs(item.startDate).tz(timezone).format('DD'),
      month: dayjs(item.startDate).tz(timezone).format('MMM'),
    },
    name: item.title,
    attendees: [],
    totalAttendees: 0,
    path: withLink
      ? getEventDedicatedPath({
          hasUser: !isPublicOffice,
          event: {
            type:
              item.type === 'network_event'
                ? 'network_events_event'
                : item.type,
            id: item.id,
          },
          topicId: item.group?.id,
          isWhitelabelGroup: isWhiteLabelGroup,
        })
      : undefined,
  };
};

// Individual transformers
const transformNews = (
  item: News,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.NEWS] => {
  const { displayFields, flags = {}, isWhiteLabelGroup, withLink } = options;
  const { flagDsIsolationNewsDetailPage } = flags;
  let navigationalProps: { onClick?: () => void; path?: string } = {};

  if (withLink) {
    const path =
      !isWhiteLabelGroup && item.group
        ? Routes.topic_new_path(item.group.id, item.id)
        : Routes.new_json_path(item.id);

    if (flagDsIsolationNewsDetailPage) {
      navigationalProps = {
        path,
        // @ts-expect-error onClick type will be updated within the next package release
        onClick: (event: MouseEvent) => {
          event.preventDefault();
          window.location.href = path;
        },
      };
    } else {
      navigationalProps = { path };
    }
  }

  return {
    contentType: CONTENT_TYPES.NEWS,
    content: item.sanitizedContent,
    displayFields: displayFields?.[CONTENT_TYPES.NEWS],
    id: item.id,
    image: item.coverPicture?.mediumUrl,
    title: item.subject,
    ...navigationalProps,
  };
};

const transformForumDiscussion = (
  item: ForumDiscussion,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.FORUM] => {
  const { displayFields, withLink } = options;
  const path = withLink
    ? makeForumUrls(item?.group?.id).discussionUrl(item.id)
    : undefined;

  return {
    contentType: CONTENT_TYPES.FORUM,
    author: {
      id: item.user.id,
      name: item.user.name,
      photoThumbUrl: item.user.photo.thumbnailUrl,
    },
    content: item.sanitizedContent,
    displayFields: displayFields?.[CONTENT_TYPES.FORUM],
    id: item.id,
    imageSrc: item.coverPicture?.mediumUrl,
    name: item.title,
    path,
  };
};

const transformUserEvent = (
  item: UserEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT] => ({
  image: item.logo?.mediumUrl,
  ...transformCommonEventData(item, options),
});

const transformAdminEvent = (
  item: AdminEvent,
  options: TransformerOptions,
): ItemTypeMap[CONTENT_TYPES.EVENT] => ({
  onlineEvent: Boolean(item.webinarUrl || item.isInternalZoomMeeting),
  image: item.coverPicture?.mediumUrl,
  ...transformCommonEventData(item, options),
});

export const transformItem = (
  item: EntityItem,
  {
    displayFields,
    timeZoneConfiguration,
    isPublicOffice = true,
    isWhiteLabelGroup,
    flags = {},
    withLink = true,
  }: TransformerOptions = {},
): ContentListItemType | undefined => {
  // Apply default options
  const opts = {
    displayFields,
    timeZoneConfiguration,
    isWhiteLabelGroup,
    isPublicOffice,
    flags,
    withLink,
  };

  switch (item.type) {
    case ENTITY_TYPES.news:
      return transformNews(item, opts);
    case ENTITY_TYPES.forumDiscussion:
      return transformForumDiscussion(item, opts);
    case ENTITY_TYPES.userEvent:
      return transformUserEvent(item, opts);
    case LEGACY_ENTITY_TYPES.adminEvent:
      return transformAdminEvent(item, opts);
    default:
      // @ts-expect-error typescript consider `item` as `never` since all known types are handled above
      // eslint-disable-next-line no-console
      console.error(`Transformer not found for item type: ${item.type}`);
  }

  return undefined;
};

export const transformDataList = (
  items: Array<EntityItem>,
  options: TransformerOptions = {},
): Array<ContentListItemType | undefined> =>
  items.map((item) => transformItem(item, options));
